import type { Timestamp } from 'firebase/firestore';
import type { SocialLinks } from '../../constants/socialPlatforms';

export const MAX_TEAM_MEMBERS = 20;
export const TEAM_TAG_MIN_LENGTH = 2;
export const TEAM_TAG_MAX_LENGTH = 5;
export const TEAM_TAG_REGEX = /^[a-z0-9]{2,5}$/;
/** Helper copy shown under the tag field. */
export const TEAM_TAG_HINT = '2–5 letters or numbers (shown as TAG)';

export type { SocialLinks };

export type TeamRole = 'owner' | 'admin' | 'member';
export type TeamVisibility = 'public' | 'private';
export type TeamInviteStatus = 'pending' | 'accepted' | 'declined' | 'revoked' | 'cancelled';
export type TeamRequestStatus = 'pending' | 'accepted' | 'refused' | 'cancelled';

export interface TeamMember {
  uid: string;
  role: TeamRole;
  shareCollection: boolean;
  joinedAt: Timestamp | Date;
  displayName?: string;
  slug?: string;
  avatarUrl?: string | null;
}

export interface Team {
  id?: string;
  name: string;
  slug: string;
  description: string;
  avatarUrl: string | null;
  bannerImageUrl?: string | null;
  countryCode?: string | null;
  /** Same platforms as player profiles (X, Discord, Twitch, etc.). */
  socialLinks?: SocialLinks;
  ownerId: string;
  members: TeamMember[];
  /** Denormalized for Firestore rules. Includes owner. */
  memberUids: string[];
  /** Denormalized for Firestore rules. Admins only (not owner). */
  adminUids: string[];
  visibility: TeamVisibility;
  /** Explicitly featured deck on the team profile hero; null when unset. */
  featuredDeckId?: string | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

export interface TeamInvite {
  id?: string;
  teamId: string;
  inviteeId: string;
  inviterId: string;
  status: TeamInviteStatus;
  createdAt: Timestamp | Date;
  respondedAt: Timestamp | Date | null;
}

export interface TeamRequest {
  id?: string;
  teamId: string;
  requesterId: string;
  status: TeamRequestStatus;
  /** Requester consent: include collection in team aggregate. Default true. */
  shareCollection: boolean;
  createdAt: Timestamp | Date;
  respondedAt: Timestamp | Date | null;
  respondedBy?: string;
  /** Denormalized at request time — admins cannot read users/{requesterId}. */
  requesterName?: string;
  requesterSlug?: string;
  requesterAvatarUrl?: string | null;
}

/** World-readable stub for browse + private link preview. */
export interface PublicTeamDirectoryEntry {
  teamId: string;
  name: string;
  slug: string;
  avatarUrl: string | null;
  bannerImageUrl?: string | null;
  description?: string;
  socialLinks?: SocialLinks;
  visibility: TeamVisibility;
  countryCode?: string | null;
  memberCount: number;
  /** Denormalized from the team doc for public hero reads. */
  featuredDeckId?: string | null;
  updatedAt: Timestamp | Date;
}

/**
 * Shape marker for `sharedCollections` documents, mirroring
 * `COLLECTION_BAG_VERSION`. Version 1 is the card-id-keyed map; documents with
 * no `v` are the original `Array<[cardId, qty, foil]>` tuples and are rewritten
 * on the owner's next session.
 */
export const TEAM_PROJECTION_VERSION = 1;

/** One card in a shared projection: quantity and foil quantity. */
export interface TeamSharedCollectionRow {
  q: number;
  f: number;
}

/** Compact shared collection projection for teammates. */
export interface TeamSharedCollection {
  uid: string;
  displayName?: string;
  slug?: string;
  /**
   * Card-id-keyed so a single card can be patched without re-uploading the
   * whole collection. Absent on legacy tuple-array documents.
   */
  v?: number;
  entries: Record<string, TeamSharedCollectionRow>;
  /** Matches `users/{uid}.collectionSummary.revision` when last written. */
  revision?: number;
  updatedAt: Timestamp | Date;
}

/**
 * A projection as it may actually be stored: either the current map shape or a
 * legacy tuple array. Normalize with `normalizeSharedProjection` at the
 * snapshot boundary — never let this reach rendering code.
 */
export type StoredTeamSharedCollection = Omit<TeamSharedCollection, 'entries'> & {
  entries: Record<string, TeamSharedCollectionRow> | Array<[string, number, number]>;
};

export function deriveMemberUids(members: TeamMember[]): string[] {
  return members.map((m) => m.uid);
}

export function deriveAdminUids(members: TeamMember[]): string[] {
  return members.filter((m) => m.role === 'admin').map((m) => m.uid);
}

export function sanitizeTeamTag(value: string): string {
  return value
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, TEAM_TAG_MAX_LENGTH)
    .toLowerCase();
}

/** User-facing error for an empty or out-of-range tag, or null when valid. */
export function teamTagValidationError(value: string): string | null {
  const tag = sanitizeTeamTag(value);
  if (!tag) return 'Team tag is required.';
  if (tag.length < TEAM_TAG_MIN_LENGTH) {
    return 'Tag must be at least 2 letters or numbers.';
  }
  if (!TEAM_TAG_REGEX.test(tag)) {
    return 'Tag must be 2–5 letters or numbers.';
  }
  return null;
}
